import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  HistoryDetailDto,
  SupplierHistoryClient,
  SupplierHistoryDto,
} from '@/api/supplier-history-client';
import { useSupplierHistoryClient } from '@/api/supplier-history-client-context';

// =============================================================================
// use-supplier-history.ts — Hooks TanStack Query de l'HISTORIQUE FOURNISSEUR (DBU §3.9, Lot A).
// =============================================================================
// Lecture PURE (useQuery). Le client effectif vient du SupplierHistoryClientProvider (doublable).
// ZÉRO règle métier : l'autorité (session/withTenant, tri par défaut/statut/agrégation) est l'API. Aucune
// donnée métier en localStorage (RULE_REF_006).

const SUPPLIER_HISTORY_KEY = ['supplier-history'] as const;

/** Clé de query du tableau « Mes participations ». */
export function supplierHistoryKey(): readonly unknown[] {
  return [...SUPPLIER_HISTORY_KEY];
}

/** Clé de query d'une vue détail (par campagne). */
export function supplierHistoryDetailKey(campaignId: string): readonly unknown[] {
  return [...SUPPLIER_HISTORY_KEY, 'detail', campaignId];
}

/** GET tableau « Mes participations » (toutes campagnes du fournisseur en session). */
export function useSupplierHistory(): UseQueryResult<SupplierHistoryDto, Error> {
  const client: SupplierHistoryClient = useSupplierHistoryClient();
  return useQuery<SupplierHistoryDto, Error>({
    queryKey: supplierHistoryKey(),
    queryFn: () => client.listParticipations(),
  });
}

/**
 * GET vue détaillée (LECTURE SEULE) d'une participation. Activée seulement quand un `campaignId` est
 * sélectionné (état d'URL) : `enabled` évite l'appel tant qu'aucune ligne n'est ouverte.
 */
export function useSupplierHistoryDetail(
  campaignId: string | null,
): UseQueryResult<HistoryDetailDto, Error> {
  const client: SupplierHistoryClient = useSupplierHistoryClient();
  return useQuery<HistoryDetailDto, Error>({
    queryKey: supplierHistoryDetailKey(campaignId ?? ''),
    queryFn: () => client.getDetail(campaignId as string),
    enabled: campaignId !== null && campaignId.length > 0,
  });
}
